/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React, { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import Banner from '../../../../components/banner';
import { BannerAnalytics } from '../../../../analytics';
import BannerPlaceholder from './BannerPlaceholder';
import LoggerUtil from '../../../../utils/LoggerUtil';
import { APPS_COMPONENT_ID } from '../../../../utils/enums';

const BANNER_PLACEHOLDER_TIMEOUT_DURATION = 10000;

@observer class Banners extends Component {
    constructor(props) {
        super(props);
        this.state = {
            placeholderTransitionFinished: false,
        };
        this.placeholderRef = React.createRef();
        setTimeout(() => {
            this.slidePlaceholderOutOfView();
        }, BANNER_PLACEHOLDER_TIMEOUT_DURATION);
    }

    slidePlaceholderOutOfView = () => {
        const { placeholderRef } = this;
        if (placeholderRef.current) {
            placeholderRef.current.style['max-height'] = '0';
            placeholderRef.current.addEventListener('transitionend', () => {
                this.setState({ placeholderTransitionFinished: true });
            });
        }
    }

    render() {
        const {
            bannersModel, appSection, subSection = {}, bannerLayouts,
            appConfig, appsSkeletonModel, allAppsModel, ...routingProps
        } = this.props;

        if (appSection.hideBanner) {
            LoggerUtil.info(`${APPS_COMPONENT_ID}: Banners: Banner is set
             to hidden for this section`);
            return null;
        }
        const appSectionFilter = appSection.id;
        const subSectionFilter = subSection.id;
        const filteredBanners = bannersModel.filterBanners({ appSectionFilter, subSectionFilter });
        const { placeholderRef } = this;
        let showBannerPlaceholder = true;

        BannerAnalytics.setBannerSource('apps');
        BannerAnalytics.setSectionId(appSectionFilter, subSectionFilter);

        if (filteredBanners.length) {
            if (placeholderRef.current) {
                placeholderRef.current.style.position = 'absolute';
                placeholderRef.current.style.opacity = 0;
                placeholderRef.current.addEventListener('transitionend', () => { this.setState({ placeholderTransitionFinished: true }); });
            } else {
                showBannerPlaceholder = false;
            }
            BannerAnalytics.setCurrentRoutingProps(routingProps);
        } else {
            const bannerDataFetched = bannersModel.hasBannerLoaded(appSectionFilter);
            if (bannerDataFetched) {
                if (placeholderRef.current) {
                    this.slidePlaceholderOutOfView();
                } else {
                    showBannerPlaceholder = false;
                }
            }
            LoggerUtil.info(`${APPS_COMPONENT_ID}: Banners: No banner applicable for this section`);
        }

        const { placeholderTransitionFinished } = this.state;
        return (
            <Fragment>
                {showBannerPlaceholder && !placeholderTransitionFinished && <BannerPlaceholder key="banner-placeholder" ref={placeholderRef} />}
                {filteredBanners.length ? filteredBanners.map(banner => (
                    <Banner
                        key={[appSection.id].join(subSection.id)}
                        banner={banner}
                        bannerLayouts={bannerLayouts}
                        appConfig={appConfig}
                        allAppsModel={allAppsModel}
                        appsSkeletonModel={appsSkeletonModel} />
                )) : null}
            </Fragment>
        );
    }
}

export default Banners;
